/**
 * адаптер Telegram WebApp SDK. всё через feature detection: приложение обязано
 * работать и в обычном браузере, показывая инструкцию, а не поддельного пользователя.
 */

interface TelegramWebApp {
  initData?: string;
  version?: string;
  platform?: string;
  colorScheme?: string;
  isExpanded?: boolean;
  viewportStableHeight?: number;
  ready?: () => void;
  expand?: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  openTelegramLink?: (url: string) => void;
  onEvent?: (event: string, handler: () => void) => void;
  offEvent?: (event: string, handler: () => void) => void;
  BackButton?: {
    show?: () => void;
    hide?: () => void;
    onClick?: (cb: () => void) => void;
    offClick?: (cb: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred?: (style: string) => void;
    selectionChanged?: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

function webApp(): TelegramWebApp | undefined {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp;
}

/** есть ли вообще Telegram-окружение с подписанным initData */
export function isTelegramEnvironment(): boolean {
  const wa = webApp();
  return typeof wa?.initData === 'string' && wa.initData.length > 0;
}

export function getInitData(): string | null {
  const wa = webApp();
  return typeof wa?.initData === 'string' && wa.initData.length > 0 ? wa.initData : null;
}

export function getPlatform(): string {
  return webApp()?.platform ?? 'unknown';
}

export function getVersion(): string {
  return webApp()?.version ?? '0';
}

/** сообщаем Telegram о готовности и разворачиваем на всю высоту */
export function signalReady(): void {
  const wa = webApp();
  try {
    wa?.ready?.();
    wa?.expand?.();
    // авторский интерфейс всегда светлый: тёмная тема Telegram его не перекрашивает
    wa?.setHeaderColor?.('#ffffff');
    wa?.setBackgroundColor?.('#ffffff');
  } catch {
    // старый клиент без части методов — не повод падать
  }
}

/** подписка на BackButton; возвращает функцию отписки для useEffect cleanup */
export function onBackButton(handler: () => void): () => void {
  const bb = webApp()?.BackButton;
  if (!bb?.onClick || !bb.show) return () => {};
  try {
    bb.onClick(handler);
    bb.show();
  } catch {
    return () => {};
  }
  return () => {
    try {
      bb.offClick?.(handler);
      bb.hide?.();
    } catch {
      // клиент уже закрыт
    }
  };
}

export function hideBackButton(): void {
  try {
    webApp()?.BackButton?.hide?.();
  } catch {
    // нечего прятать
  }
}

/** ссылку t.me открываем средствами Telegram: так она не закрывает мини-приложение */
export function openTelegramLink(url: string): void {
  try {
    const wa = webApp();
    if (wa?.openTelegramLink && url.startsWith('https://t.me/')) {
      wa.openTelegramLink(url);
      return;
    }
  } catch {
    // старый клиент — открываем как обычную ссылку
  }
  window.open(url, '_blank', 'noopener');
}

export function haptic(kind: 'light' | 'medium' | 'select' = 'light'): void {
  try {
    const hf = webApp()?.HapticFeedback;
    if (kind === 'select') hf?.selectionChanged?.();
    else hf?.impactOccurred?.(kind);
  } catch {
    // тактильная отдача необязательна
  }
}

/** подписка на события SDK с гарантированной отпиской */
export function onWebAppEvent(event: string, handler: () => void): () => void {
  const wa = webApp();
  if (!wa?.onEvent) return () => {};
  try {
    wa.onEvent(event, handler);
  } catch {
    return () => {};
  }
  return () => {
    try {
      wa.offEvent?.(event, handler);
    } catch {
      // клиент уже закрыт
    }
  };
}

/** зона пользователя по браузеру; подтверждение остаётся за пользователем */
export function guessTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
